import { useEffect } from 'react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { isNotesEmpty } from '../../utils/notes';
import classes from './NotesRichText.module.css';

interface NotesRichTextProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
}

export function NotesRichText({ value, onChange, disabled }: NotesRichTextProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value ?? '',
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(isNotesEmpty(html) ? null : html);
    },
  });

  // Keep the editor's editable state in sync with the report's editability.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Sync external value changes (e.g. after a save refetch) into the editor.
  useEffect(() => {
    if (!editor) {
      return;
    }
    const current = editor.getHTML();
    const next = value ?? '';
    const bothEmpty = isNotesEmpty(current) && isNotesEmpty(next);
    if (current !== next && !bothEmpty) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <RichTextEditor editor={editor} classNames={{ root: classes.root, content: classes.content }}>
      <RichTextEditor.Toolbar>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>
      <RichTextEditor.Content />
    </RichTextEditor>
  );
}
